import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Download, Trash2, ListFilter, AlertTriangle } from "lucide-react";
import { api, pesanGalat, fmtNum } from "../../../lib/api";
import { formatTanggal } from "../../../lib/tanggal";
import {
  MDR_BASE, Modal, DialogAlasan, unduhDenganToast, kelasTombol, kelasTombolUtama, kelasTombolKecil,
} from "./shared";

/** Impor Data Claim — SATU tombol untuk semua tab (brief C.3/C.10, keputusan
 *  C6). Sistem yang menentukan tab dari Leasing/CC + distributor unit; finance
 *  tidak memisah berkas per TAM/GDN. Baris gagal dilaporkan per nomor baris
 *  (bukan ditelan), No. Kontrak yang sudah pernah diimpor ditolak server.
 *
 *  Riwayat impor di bawahnya: "Tampilkan" menyaring tabel ke satu impor
 *  (filter di SERVER, `impor_id`), "Hapus" hanya selama belum ada barisnya
 *  yang Siap/Sudah Claim — server yang memutuskan (409 kalau sudah). */
export default function DialogImporData({ hak, onTutup, onSelesai, onPilihImpor }) {
  const [berkas, setBerkas] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasil, setHasil] = useState(null);
  const [riwayat, setRiwayat] = useState([]);
  const [memuat, setMemuat] = useState(true);
  const [rincian, setRincian] = useState(null);
  const [hapus, setHapus] = useState(null);
  // Kunci input berkas: diganti sesudah impor supaya pemilih berkas kosong lagi
  // (nilai <input type=file> tidak bisa dikosongkan lewat state React).
  const [kunciBerkas, setKunciBerkas] = useState(0);

  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const r = await api.get(`${MDR_BASE}/impor-data`);
      setRiwayat(r.data.items || []);
    } catch (e) { toast.error(pesanGalat(e, "Gagal memuat riwayat impor")); }
    finally { setMemuat(false); }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  const impor = async () => {
    if (!berkas) return;
    setBusy(true); setHasil(null);
    try {
      const fd = new FormData();
      fd.append("file", berkas);
      const r = await api.post(`${MDR_BASE}/impor-data`, fd, { timeout: 180000 });
      setHasil(r.data);
      toast.success(r.data.message || "Impor selesai");
      setBerkas(null);
      setKunciBerkas((n) => n + 1);
      muat();
      onSelesai?.();
    } catch (e) { toast.error(pesanGalat(e, "Impor gagal")); }
    finally { setBusy(false); }
  };

  const lihatRincian = async (iid) => {
    try {
      const r = await api.get(`${MDR_BASE}/impor-data/${iid}`);
      setRincian(r.data.item);
    } catch (e) { toast.error(pesanGalat(e, "Gagal memuat rincian impor")); }
  };

  const hapusImpor = async (alasan) => {
    try {
      const r = await api.delete(`${MDR_BASE}/impor-data/${hapus.id}`, { params: { alasan } });
      toast.success(r.data.message || "Impor dihapus");
      setHapus(null);
      muat();
      onSelesai?.();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menghapus impor")); }
  };

  return (
    <Modal judul="Import Data Claim" ikon={FileSpreadsheet} onTutup={onTutup} lebar="max-w-3xl" testid="mdr-impor-data"
           sub="Satu berkas untuk semua tab — tab ditentukan dari Leasing/CC dan distributor unit.">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={kelasTombol} data-testid="mdr-impor-data-template"
                  onClick={() => unduhDenganToast(`${MDR_BASE}/impor-data/template`, "template-data-claim-mdr.xlsx")}>
            <Download className="w-4 h-4" /> Unduh Template
          </button>
          {hak.impor && (
            <>
              <input key={kunciBerkas} type="file" accept=".xlsx" data-testid="mdr-impor-data-berkas"
                     onChange={(e) => setBerkas(e.target.files?.[0] || null)}
                     className="text-sm file:mr-2 file:h-9 file:px-3 file:rounded-lg file:border file:border-slate-200 file:bg-white file:text-sm" />
              <button type="button" onClick={impor} disabled={!berkas || busy} data-testid="mdr-impor-data-kirim"
                      className={kelasTombolUtama}>
                <Upload className="w-4 h-4" /> {busy ? "Mengimpor..." : "Impor"}
              </button>
            </>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Wajib: Tanggal Pembelian, Tipe Produk, Tenor, Nominal, Leasing/CC. SRP &amp; DP diisi hanya untuk leasing
          prioritas yang memakai SRP/DP vendor — kosongkan untuk memakai Master SRP Promo. No. Kontrak/Order ID
          (opsional) menolak impor ganda.
        </p>

        {hasil && <HasilImpor hasil={hasil} />}

        <div>
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Riwayat Impor</p>
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-2 py-2">Kode</th>
                  <th className="text-left px-2 py-2">Berkas</th>
                  <th className="text-left px-2 py-2">Tanggal / Oleh</th>
                  <th className="text-right px-2 py-2">Diterima</th>
                  <th className="text-right px-2 py-2">Ditolak</th>
                  <th className="text-left px-2 py-2">Status</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {memuat ? (
                  <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-400">Memuat...</td></tr>
                ) : riwayat.length === 0 ? (
                  <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-400">Belum ada impor</td></tr>
                ) : riwayat.map((r) => (
                  <tr key={r.id} data-testid={`mdr-impor-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-mono">{r.kode}</td>
                    <td className="px-2 py-1.5 max-w-[160px] truncate" title={r.nama_file}>{r.nama_file || "-"}</td>
                    <td className="px-2 py-1.5">{formatTanggal(r.created_at, { denganJam: true })}<br />
                      <span className="text-slate-400">{r.created_by_name || r.created_by}</span></td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtNum(r.diterima)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtNum(r.ditolak)}</td>
                    <td className="px-2 py-1.5 capitalize">{r.status}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex justify-end gap-1 flex-wrap">
                        {r.status === "aktif" && (
                          <button type="button" className={kelasTombolKecil} data-testid={`mdr-impor-tampilkan-${r.id}`}
                                  onClick={() => { onPilihImpor?.(r); onTutup(); }}>
                            <ListFilter className="w-3.5 h-3.5" /> Tampilkan
                          </button>
                        )}
                        {(r.ditolak > 0) && (
                          <button type="button" className={kelasTombolKecil} data-testid={`mdr-impor-rincian-${r.id}`}
                                  onClick={() => lihatRincian(r.id)}>
                            <AlertTriangle className="w-3.5 h-3.5" /> Baris Gagal
                          </button>
                        )}
                        {hak.hapus && r.status === "aktif" && (
                          <button type="button" data-testid={`mdr-impor-hapus-${r.id}`} onClick={() => setHapus(r)}
                                  className="h-8 px-2.5 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-medium inline-flex items-center gap-1">
                            <Trash2 className="w-3.5 h-3.5" /> Hapus
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {rincian && (
        <Modal judul={`Baris gagal — ${rincian.kode}`} onTutup={() => setRincian(null)} lebar="max-w-xl"
               testid="mdr-impor-rincian">
          <HasilImpor hasil={rincian} tanpaRingkasan />
        </Modal>
      )}
      {hapus && (
        <DialogAlasan judul={`Hapus impor ${hapus.kode}?`} bahaya labelYa="Hapus Impor" testid="mdr-impor-hapus-dialog"
                      keterangan="Semua baris impor ini ikut terhapus. Ditolak kalau ada barisnya yang sudah Siap/Sudah Claim."
                      onYa={hapusImpor} onTutup={() => setHapus(null)} />
      )}
    </Modal>
  );
}

export function HasilImpor({ hasil, tanpaRingkasan = false }) {
  const gagal = hasil.gagal || [];
  const peringatan = hasil.peringatan || [];
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="mdr-impor-hasil">
      {!tanpaRingkasan && (
        <p className="text-sm">
          <span className="font-mono font-semibold">{hasil.kode}</span>: {" "}
          <span className="text-emerald-700 font-medium">{fmtNum(hasil.diterima)} diterima</span>,{" "}
          <span className={gagal.length ? "text-rose-700 font-medium" : "text-slate-500"}>{fmtNum(hasil.ditolak)} ditolak</span>
        </p>
      )}
      {gagal.length > 0 && (
        <div className="max-h-48 overflow-y-auto">
          <p className="text-[11px] font-semibold text-rose-700 mb-1">Baris ditolak</p>
          <ul className="text-xs space-y-0.5">
            {gagal.map((g, i) => (
              <li key={i} data-testid={`mdr-impor-gagal-${g.baris}`}><span className="font-mono">Baris {g.baris}</span>: {g.alasan}</li>
            ))}
          </ul>
        </div>
      )}
      {peringatan.length > 0 && (
        <div className="max-h-40 overflow-y-auto">
          <p className="text-[11px] font-semibold text-amber-700 mb-1">Peringatan (tetap diterima)</p>
          <ul className="text-xs space-y-0.5">
            {peringatan.map((g, i) => (
              <li key={i}><span className="font-mono">Baris {g.baris}</span>: {g.alasan}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
